import { Link } from 'react-router-dom';
import type { ProfessionalOnlineStatus } from '@klaro7/shared-types';
import { Card, CardContent, CardHeader, CardTitle, Spinner, Button } from '@klaro7/ui';

import { useFavorites } from '@/features/favorites/hooks/useFavorites';
import { resolveUploadUrl } from '@/lib/uploads';

const statusConfig: Record<
  ProfessionalOnlineStatus,
  { label: string; dot: string; text: string; bg: string }
> = {
  online: {
    label: 'Online',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
  },
  busy: {
    label: 'Occupato',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
  },
  offline: {
    label: 'Offline',
    dot: 'bg-neutral-400',
    text: 'text-neutral-500',
    bg: 'bg-neutral-100',
  },
  paused: {
    label: 'In pausa',
    dot: 'bg-brand-orange',
    text: 'text-brand-orange',
    bg: 'bg-brand-orange/10',
  },
};

function StatusBadge({ status }: { status?: ProfessionalOnlineStatus }) {
  const cfg = statusConfig[status ?? 'offline'];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      <span className={`h-2 w-2 rounded-full ${cfg.dot}`} aria-hidden="true" />
      {cfg.label}
    </span>
  );
}

export function PreferitiPage() {
  const { data: favorites, isLoading } = useFavorites();

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-6">
        <nav aria-label="Percorso di navigazione" className="flex flex-wrap items-center gap-1.5 text-sm text-neutral-500">
          <Link to="/consulenti" className="rounded transition-colors hover:text-brand-teal">
            Consulenti
          </Link>
          <svg aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
          </svg>
          <span aria-current="page" className="font-medium text-neutral-900">
            I miei Preferiti
          </span>
        </nav>

        <div>
          <h1 className="text-2xl font-bold text-neutral-900">I miei Preferiti</h1>
          <p className="text-neutral-500">I consulenti che hai salvato</p>
        </div>

        <Card className="border-neutral-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-neutral-900">Consulenti preferiti</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : !favorites?.length ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 rounded-full bg-neutral-100 p-4">
                  <svg className="h-8 w-8 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                </div>
                <p className="font-medium text-neutral-600">Nessun preferito</p>
                <p className="mt-1 text-sm text-neutral-400">
                  Non hai ancora aggiunto consulenti ai preferiti
                </p>
                <Link
                  to="/consulenti"
                  className="mt-3 inline-block text-sm font-medium text-brand-teal underline underline-offset-2 hover:text-brand-teal-400"
                >
                  Sfoglia i consulenti
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {favorites.filter((item) => item.professional).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 p-3 transition-colors hover:bg-neutral-50"
                  >
                    <Link
                      to={`/consultant/${item.professionalId}`}
                      className="flex min-w-0 flex-1 items-center gap-3"
                    >
                      {item.professional?.avatar ? (
                        <img
                          src={resolveUploadUrl(item.professional.avatar)}
                          alt={`${item.professional?.firstName ?? ''} ${item.professional?.lastName ?? ''}`}
                          className="h-12 w-12 shrink-0 rounded-full border border-neutral-200 object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-neutral-100">
                          <img src="/images/logo-navy.png" alt="" className="h-7 w-7 object-contain opacity-60" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-neutral-900">
                          {item.professional?.firstName} {item.professional?.lastName}
                        </p>
                        {item.professional?.title && (
                          <p className="truncate text-xs text-neutral-500">
                            {item.professional.title}
                          </p>
                        )}
                        <div className="mt-1">
                          <StatusBadge status={item.onlineStatus} />
                        </div>
                      </div>
                    </Link>
                    <Button
                      asChild
                      variant="secondary"
                      size="sm"
                      className="shrink-0"
                    >
                      <Link to={`/consultant/${item.professionalId}`}>Apri</Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
